import { createElement, cloneElement } from 'react';

/**
 * Guirnalda de banderines de papel picado para la portada en Navidad. Es el
 * adorno más mexicano que cabe en una marca de papel: un hilo con banderines
 * de colores, cada uno con su muesca y su recorte, que se mecen apenas.
 *
 * Geometría: el hilo es una parábola (cuelga al centro) y cada banderín se
 * coloca sobre ella y se inclina con la pendiente del hilo. Todo se calcula
 * una vez al cargar el módulo con fórmulas fijas (sin aleatoriedad) para que
 * el pre-render del build y el navegador dibujen lo mismo.
 *
 * `slice` recorta por los lados en pantallas angostas en vez de deformar los
 * banderines. Decorativo puro: aria-hidden y sin eventos.
 */
const W = 1440;
const SAG = 34;       // cuánto cuelga el hilo en el centro
const EDGE_Y = 6;     // altura del hilo en los extremos
const COLORS = ['var(--nochebuena)', 'var(--oro-fiesta)', 'var(--pino)', 'var(--kraft)'];
const COUNT = 17;

const yAt = (x) => {
    const t = x / W;
    return EDGE_Y + 4 * SAG * t * (1 - t);
};

const FLAGS = Array.from({ length: COUNT }, (_, i) => {
    const x = ((i + 0.5) / COUNT) * W;
    const slope = ((yAt(x + 1) - yAt(x - 1)) / 2);
    return {
        x,
        y: yAt(x),
        angle: Math.round(Math.atan(slope) * (180 / Math.PI) * 10) / 10,
        color: COLORS[i % COLORS.length],
        delay: -((i * 0.7) % 5.5),
        swing: 2 + (i % 3) * 0.5,
    };
});

const THREAD = `M0 ${EDGE_Y} Q${W / 2} ${EDGE_Y + 2 * SAG} ${W} ${EDGE_Y}`;

// El SVG entero se construye UNA vez, a nivel de módulo y con createElement,
// no con expresiones dentro del JSX: en desarrollo la herramienta de edición
// visual envuelve cada expresión renderizada en un <span>, y un <span> dentro
// de un <svg> no se pinta. En producción esa herramienta no existe; así
// funciona igual en los dos.
const e = createElement;
const FLAG_GROUP = e(
    'g',
    null,
    ...FLAGS.map((f, i) =>
        e(
            'g',
            { key: i, transform: `translate(${f.x} ${f.y}) rotate(${f.angle})` },
            e(
                'g',
                {
                    className: 'bunting-flag',
                    style: { '--delay': `${f.delay}s`, '--r0': `${-f.swing}deg`, '--r1': `${f.swing + 0.5}deg` },
                },
                // Banderín con muesca en V abajo, como el papel picado de feria
                e('path', { d: 'M-17 0 H17 V44 L0 35 L-17 44 Z', fill: f.color }),
                // Recorte en rombo, el "picado"
                e('path', { d: 'M0 11 L5 18 L0 25 L-5 18 Z', fill: 'var(--paper)', fillOpacity: 0.92 })
            )
        )
    )
);

const BUNTING = e(
    'svg',
    {
        viewBox: `0 0 ${W} ${EDGE_Y + SAG + 52}`,
        preserveAspectRatio: 'xMidYMin slice',
        'aria-hidden': 'true',
        'data-testid': 'christmas-bunting',
    },
    e('path', { d: THREAD, fill: 'none', stroke: 'var(--ink-soft)', strokeOpacity: 0.55, strokeWidth: 1.4 }),
    FLAG_GROUP
);

export default function Bunting({ className = '' }) {
    return cloneElement(BUNTING, { className });
}
